import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useTheme } from '../../contexts/ThemeContext';
import * as XLSX from 'xlsx';

const formatDate = (dateString: string): string => {
  if (!dateString || dateString === '-') return '-';
  
  try {
    const date = new Date(dateString + 'T00:00:00');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();
    
    return `${month}/${day}/${year}`;
  } catch (error) {
    return dateString;
  }
};

const formatDateTime = (dateString: string): string => {
  if (!dateString || dateString === '-') return '-';
  
  try {
    // Check if it's already in the MM/DD/YYYY, HH:MM AM/PM format
    if (dateString.includes(',') && dateString.includes('/')) {
      return dateString;
    }
    
    // Parse ISO string and convert to local time
    const date = new Date(dateString);
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();
    let hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12;
    const hoursStr = String(hours).padStart(2, '0');
    
    return `${month}/${day}/${year}, ${hoursStr}:${minutes} ${ampm}`;
  } catch (error) {
    return dateString;
  }
};

const OnHoldPage = () => {
  const navigate = useNavigate();
  const { isDarkMode, toggleDarkMode } = useTheme();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [clients, setClients] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  // Add edit and dialog states
  const [editingId, setEditingId] = useState<string | null>(null);
  const [showClientInfoDialog, setShowClientInfoDialog] = useState(false);
  const [clientInfoDialogClient, setClientInfoDialogClient] = useState<any>(null);
  const [clientStateOptions, setClientStateOptions] = useState<string[]>([]);

  // Add client info dialog states
  const [editingClientInfoEmail, setEditingClientInfoEmail] = useState(false);
  const [editingClientInfoPhone, setEditingClientInfoPhone] = useState(false);
  const [clientInfoEmail, setClientInfoEmail] = useState<string>('');
  const [clientInfoPhone, setClientInfoPhone] = useState<string>('');
  
  // Notes management state
  const [clientNotes, setClientNotes] = useState<any[]>([]);
  const [newNoteText, setNewNoteText] = useState<string>('');
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const [editingNoteText, setEditingNoteText] = useState<string>('');
  
  // Sent templates state
  const [sentTemplates, setSentTemplates] = useState<any[]>([]);
  
  // Discovery calls state
  const [clientDiscoveryCalls, setClientDiscoveryCalls] = useState<any[]>([]);

  // Add user profile state
  const [userFullName, setUserFullName] = useState<string>('');

  // Add loadClientStates function
  const loadClientStates = async () => {
    const { data, error } = await supabase
      .from('client_states')
      .select('*')
      .order('display_order', { ascending: true });

    if (error) {
      console.error('Error loading client states:', error);
      return;
    }

    if (data && data.length > 0) {
      setClientStateOptions(data.map(s => s.state_name));
    } else {
      // Fallback to default states if none in database
      setClientStateOptions([
        'Lead Received; Not Yet Contacted',
        'Lead Received; Contacted; No Response',
        'Application Sent, Awaiting Completion',
        'Application Completed; Pre-Approval/VAL in Process',
        'Pre-Approved/VAL Complete; House Shopping',
        'Offer Accepted; Loan In Process',
        'Closed',
        'On Hold; Will Contact at Later Date',
        'Not Interested; Dead Deal'
      ]);
    }
  };

  // Check authentication
  useEffect(() => {
    const checkAuth = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        navigate('/login');
      } else {
        // Load user profile name
        const metadata = session.user.user_metadata;
        const firstName = metadata.first_name || '';
        const lastName = metadata.last_name || '';
        setUserFullName(`${firstName} ${lastName}`.trim() || 'User');
      }
    };
    checkAuth();
  }, [navigate]);

  // Fetch clients with "On Hold" status
  useEffect(() => {
    const fetchClients = async () => {
      try {
        setLoading(true);
        const { data, error } = await supabase
          .from('clients')
          .select('*')
          .eq('client_state', 'On Hold; Will Contact at Later Date')
          .order('created_at', { ascending: false });

        if (error) throw error;
        setClients(data || []);
      } catch (error) {
        console.error('Error fetching clients:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchClients();
    loadClientStates();
  }, []);

  // Add handleEdit function
  const handleEdit = (id: string) => {
    setEditingId(id);
  };

  // Add handleSave function
  const handleSave = async (id: string) => {
    const client = clients.find(c => c.id === id);
    if (!client) return;

    const { error } = await supabase
      .from('clients')
      .update({
        client_state: client.client_state,
        follow_up_date: client.follow_up_date,
        notes: client.notes
      })
      .eq('id', id);

    if (error) {
      console.error('Error updating client:', error);
      alert('Failed to update client');
      return;
    }

    setEditingId(null);
  };

  // Add handleChange function
  const handleChange = (id: string, field: string, value: string) => {
    setClients(clients.map(c => {
      if (c.id === id) {
        return { ...c, [field]: value };
      }
      return c;
    }));
  };

  const handleClientNameClick = async (client: any) => {
    // Load all data BEFORE showing the dialog
    const [notesData, templatesData, discoveryData] = await Promise.all([
      supabase
        .from('client_notes')
        .select('*')
        .eq('client_id', client.id)
        .order('created_at', { ascending: false }),
      supabase
        .from('sent_templates')
        .select('*')
        .eq('client_id', client.id)
        .order('sent_at', { ascending: false }),
      supabase
        .from('discovery_calls')
        .select('*')
        .eq('client_id', client.id)
        .order('call_date', { ascending: false })
    ]);

    // Set all the data
    setClientInfoDialogClient(client);
    setClientInfoEmail(client.email);
    setClientInfoPhone(client.phone);
    setEditingClientInfoEmail(false);
    setEditingClientInfoPhone(false);
    
    if (notesData.data) {
      setClientNotes(notesData.data);
    }
    
    if (templatesData.data) {
      setSentTemplates(templatesData.data);
    }
    
    if (discoveryData.data) {
      setClientDiscoveryCalls(discoveryData.data);
    }
    
    setNewNoteText('');
    setEditingNoteId(null);
    setEditingNoteText('');
    
    // NOW show the dialog
    setShowClientInfoDialog(true);
  };

  const handleAddNote = async () => {
    if (!clientInfoDialogClient || !newNoteText.trim()) return;

    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return;

    const { data, error } = await supabase
      .from('client_notes')
      .insert({
        client_id: clientInfoDialogClient.id,
        note: newNoteText.trim(),
        user_id: session.user.id
      })
      .select()
      .single();

    if (error) {
      console.error('Error adding note:', error);
      alert('Failed to add note');
      return;
    }

    if (data) {
      setClientNotes([data, ...clientNotes]);
      setNewNoteText('');
    }
  };

  const handleEditNote = (note: any) => {
    setEditingNoteId(note.id);
    setEditingNoteText(note.note);
  };

  const handleSaveNote = async (noteId: string) => {
    if (!editingNoteText.trim()) return;

    const { error } = await supabase
      .from('client_notes')
      .update({
        note: editingNoteText.trim(),
        updated_at: new Date().toISOString()
      })
      .eq('id', noteId);

    if (error) {
      console.error('Error updating note:', error);
      alert('Failed to update note');
      return;
    }

    setClientNotes(clientNotes.map(n => 
      n.id === noteId 
        ? { ...n, note: editingNoteText.trim(), updated_at: new Date().toISOString() }
        : n
    ));
    setEditingNoteId(null);
    setEditingNoteText('');
  };

  const handleDeleteNote = async (noteId: string) => {
    if (!confirm('Are you sure you want to delete this note?')) return;

    const { error } = await supabase
      .from('client_notes')
      .delete()
      .eq('id', noteId);

    if (error) {
      console.error('Error deleting note:', error);
      alert('Failed to delete note');
      return;
    }

    setClientNotes(clientNotes.filter(n => n.id !== noteId));
  };

  const handleCancelEditNote = () => {
    setEditingNoteId(null);
    setEditingNoteText('');
  };

  const handleSaveClientInfo = async () => {
    if (!clientInfoDialogClient) return;

    // Validate phone number
    const phoneDigits = clientInfoPhone.replace(/\D/g, '');
    if (phoneDigits.length !== 10) {
      alert('Please enter a valid 10-digit phone number');
      return;
    }

    // Validate email
    if (!clientInfoEmail || !clientInfoEmail.includes('@')) {
      alert('Please enter a valid email address');
      return;
    }

    const { error } = await supabase
      .from('clients')
      .update({
        email: clientInfoEmail,
        phone: clientInfoPhone
      })
      .eq('id', clientInfoDialogClient.id);

    if (error) {
      console.error('Error updating client info:', error);
      alert('Failed to update client information');
      return;
    }

    setClients(clients.map(c => 
      c.id === clientInfoDialogClient.id 
        ? { ...c, email: clientInfoEmail, phone: clientInfoPhone }
        : c
    ));

    setEditingClientInfoEmail(false);
    setEditingClientInfoPhone(false);
  };

  const handleClientInfoPhoneChange = (value: string) => {
    const digits = value.replace(/\D/g, '');
    if (digits.length <= 10) {
      let formattedValue = digits;
      if (digits.length >= 6) {
        formattedValue = `(${digits.slice(0, 3)})-${digits.slice(3, 6)}-${digits.slice(6)}`;
      } else if (digits.length >= 3) {
        formattedValue = `(${digits.slice(0, 3)})-${digits.slice(3)}`;
      }
      setClientInfoPhone(formattedValue);
    }
  };

  const handleDeleteSentTemplate = async (templateId: string) => {
    if (confirm('Are you sure you want to delete this sent template record?')) {
      const { error } = await supabase
        .from('sent_templates')
        .delete()
        .eq('id', templateId);

      if (error) {
        console.error('Error deleting sent template:', error);
        alert('Failed to delete sent template');
        return;
      }

      setSentTemplates(sentTemplates.filter(t => t.id !== templateId));
    }
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/login');
  };
  
  // Calculate stats
  const totalOnHold = clients.length;
  const nsClients = clients.filter(c => c.lender_name === 'NS');
  const tmClients = clients.filter(c => c.lender_name === 'TM');
  
  const totalPurchasePrice = clients.reduce((sum, c) => sum + (parseFloat(c.purchase_price) || 0), 0);
  const totalLoanAmount = clients.reduce((sum, c) => sum + (parseFloat(c.loan_amount) || 0), 0);
  
  const nsPurchasePrice = nsClients.reduce((sum, c) => sum + (parseFloat(c.purchase_price) || 0), 0);
  const nsLoanAmount = nsClients.reduce((sum, c) => sum + (parseFloat(c.loan_amount) || 0), 0);
  
  const tmPurchasePrice = tmClients.reduce((sum, c) => sum + (parseFloat(c.purchase_price) || 0), 0);
  const tmLoanAmount = tmClients.reduce((sum, c) => sum + (parseFloat(c.loan_amount) || 0), 0);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const handleExportToExcel = () => {
    // Prepare data for Excel
    const exportData = clients.map(client => ({
      'Client Name': client.name,
      'State': client.state || '-',
      'Purchase Price': formatCurrency(parseFloat(client.purchase_price) || 0),
      'Loan Amount': formatCurrency(parseFloat(client.loan_amount) || 0),
      'Loan Type': client.loan_type || 'N/A',
      'Lender': client.lender_name || '-',
      'Broker': client.broker,
      'Follow-Up Date': client.follow_up_date ? formatDate(client.follow_up_date) : '-',
      'Client State': client.client_state,
      'Notes': client.notes || '-'
    }));

    // Create workbook and worksheet
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(exportData);

    // Auto-size columns
    const colWidths = Object.keys(exportData[0] || {}).map(key => ({
      wch: Math.max(key.length, 15)
    }));
    ws['!cols'] = colWidths;

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(wb, ws, 'On Hold Clients');

    // Generate filename with current date
    const filename = `On_Hold_Clients_${new Date().toISOString().split('T')[0]}.xlsx`;

    // Write file
    XLSX.writeFile(wb, filename, { 
      bookType: 'xlsx',
      type: 'binary',
      compression: true
    });
  };

  return (
    <div className={`min-h-screen ${isDarkMode ? 'bg-black' : 'bg-gray-50'}`}>
      {/* Navigation */}
      <nav className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} shadow-sm border-b`}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 flex items-center justify-center">
              <i className="ri-home-heart-line text-3xl text-navy"></i>
            </div>
            <a href="/" className={`text-xl font-bold ${isDarkMode ? 'text-gray-200' : 'text-navy'} hover:opacity-80 transition-opacity cursor-pointer`}>{userFullName}</a>
          </div>
          
          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-4">
            <a href="/" className={`${isDarkMode ? 'text-gray-300 hover:text-gray-100' : 'text-gray-600 hover:text-navy'} transition-colors font-medium whitespace-nowrap cursor-pointer`}>
              <i className="ri-home-5-line mr-2"></i>
              Home
            </a>
            <a href="/crm" className={`${isDarkMode ? 'text-gray-300 hover:text-gray-100' : 'text-gray-600 hover:text-navy'} transition-colors font-medium whitespace-nowrap cursor-pointer`}>
              <i className="ri-contacts-book-2-line mr-2"></i>
              CRM
            </a>
            
            {/* Pages Dropdown */}
            <div className="relative group">
              <button className={`${isDarkMode ? 'text-gray-300 hover:text-gray-100' : 'text-gray-600 hover:text-navy'} transition-colors font-medium whitespace-nowrap cursor-pointer flex items-center gap-1`}>
                <i className="ri-pages-line mr-2"></i>
                Pages
                <i className="ri-arrow-down-s-line"></i>
              </button>
              <div className={`absolute top-full left-0 mt-1 ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} border rounded-lg shadow-lg py-2 min-w-[200px] opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50`}>
                <a href="/closed" className={`block px-4 py-2 ${isDarkMode ? 'text-gray-300 hover:bg-gray-700 hover:text-gray-100' : 'text-gray-600 hover:bg-gray-50 hover:text-navy'} transition-colors cursor-pointer`}>
                  <i className="ri-archive-line mr-2"></i>
                  Closed
                </a>
                <a href="/dead-deals" className={`block px-4 py-2 ${isDarkMode ? 'text-gray-300 hover:bg-gray-700 hover:text-gray-100' : 'text-gray-600 hover:bg-gray-50 hover:text-navy'} transition-colors cursor-pointer`}>
                  <i className="ri-close-circle-line mr-2"></i>
                  Dead Deals
                </a>
                <a href="/on-hold" className={`block px-4 py-2 ${isDarkMode ? 'text-teal' : 'text-navy'} ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'} font-semibold cursor-pointer`}>
                  <i className="ri-pause-circle-line mr-2"></i>
                  On Hold
                </a>
                <a href="/templates" className={`block px-4 py-2 ${isDarkMode ? 'text-gray-300 hover:bg-gray-700 hover:text-gray-100' : 'text-gray-600 hover:bg-gray-50 hover:text-navy'} transition-colors cursor-pointer`}>
                  <i className="ri-file-text-line mr-2"></i>
                  Templates
                </a>
                <a href="/intake" className={`block px-4 py-2 ${isDarkMode ? 'text-gray-300 hover:bg-gray-700 hover:text-gray-100' : 'text-gray-600 hover:bg-gray-50 hover:text-navy'} transition-colors cursor-pointer`}>
                  <i className="ri-inbox-line mr-2"></i>
                  Message Intake
                </a>
                <a href="/history" className={`block px-4 py-2 ${isDarkMode ? 'text-gray-300 hover:bg-gray-700 hover:text-gray-100' : 'text-gray-600 hover:bg-gray-50 hover:text-navy'} transition-colors cursor-pointer`}>
                  <i className="ri-history-line mr-2"></i>
                  History
                </a>
                <a href="/admin" className={`block px-4 py-2 ${isDarkMode ? 'text-gray-300 hover:bg-gray-700 hover:text-gray-100' : 'text-gray-600 hover:bg-gray-50 hover:text-navy'} transition-colors cursor-pointer`}>
                  <i className="ri-settings-3-line mr-2"></i>
                  Admin
                </a>
              </div>
            </div>
            
            {/* Dark Mode Toggle */}
            <button
              onClick={toggleDarkMode}
              className={`${isDarkMode ? 'text-gray-300 hover:text-gray-100' : 'text-gray-600 hover:text-navy'} transition-colors font-medium whitespace-nowrap cursor-pointer`}
              title={isDarkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
            >
              <i className={`${isDarkMode ? 'ri-sun-line' : 'ri-moon-line'} text-xl`}></i>
            </button>
            
            <button
              onClick={handleLogout}
              className="text-red-600 hover:text-red-700 transition-colors font-medium whitespace-nowrap cursor-pointer"
            >
              <i className="ri-logout-box-line mr-2"></i>
              Logout
            </button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className={`md:hidden w-10 h-10 flex items-center justify-center ${isDarkMode ? 'text-gray-300' : 'text-gray-600'} hover:text-navy transition-colors cursor-pointer`}
          >
            <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className={`md:hidden border-t ${isDarkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'}`}
          >
            <div className="px-6 py-4 space-y-3">
              <a href="/" className={`block py-2 ${isDarkMode ? 'text-gray-300 hover:text-gray-100' : 'text-gray-600 hover:text-navy'} transition-colors font-medium cursor-pointer`}>
                <i className="ri-home-5-line mr-2"></i>
                Home
              </a>
              <a href="/crm" className={`block py-2 ${isDarkMode ? 'text-gray-300 hover:text-gray-100' : 'text-gray-600 hover:text-navy'} transition-colors font-medium cursor-pointer`}>
                <i className="ri-contacts-book-2-line mr-2"></i>
                CRM
              </a>
              <a href="/closed" className={`block py-2 ${isDarkMode ? 'text-gray-300 hover:text-gray-100' : 'text-gray-600 hover:text-navy'} transition-colors font-medium cursor-pointer`}>
                <i className="ri-archive-line mr-2"></i>
                Closed
              </a>
              <a href="/dead-deals" className={`block py-2 ${isDarkMode ? 'text-gray-300 hover:text-gray-100' : 'text-gray-600 hover:text-navy'} transition-colors font-medium cursor-pointer`}>
                <i className="ri-close-circle-line mr-2"></i>
                Dead Deals
              </a>
              <a href="/on-hold" className={`block py-2 ${isDarkMode ? 'text-teal' : 'text-navy'} font-semibold cursor-pointer`}>
                <i className="ri-pause-circle-line mr-2"></i>
                On Hold
              </a>
              <a href="/templates" className={`block py-2 ${isDarkMode ? 'text-gray-300 hover:text-gray-100' : 'text-gray-600 hover:text-navy'} transition-colors font-medium cursor-pointer`}>
                <i className="ri-file-text-line mr-2"></i>
                Templates
              </a>
              <a href="/intake" className={`block py-2 ${isDarkMode ? 'text-gray-300 hover:text-gray-100' : 'text-gray-600 hover:text-navy'} transition-colors font-medium cursor-pointer`}>
                <i className="ri-inbox-line mr-2"></i>
                Message Intake
              </a>
              <a href="/history" className={`block py-2 ${isDarkMode ? 'text-gray-300 hover:text-gray-100' : 'text-gray-600 hover:text-navy'} transition-colors font-medium cursor-pointer`}>
                <i className="ri-history-line mr-2"></i>
                History
              </a>
              <a href="/admin" className={`block py-2 ${isDarkMode ? 'text-gray-300 hover:text-gray-100' : 'text-gray-600 hover:text-navy'} transition-colors font-medium cursor-pointer`}>
                <i className="ri-settings-3-line mr-2"></i>
                Admin
              </a>
              <button
                onClick={toggleDarkMode}
                className={`block w-full text-left py-2 ${isDarkMode ? 'text-gray-300 hover:text-gray-100' : 'text-gray-600 hover:text-navy'} transition-colors font-medium cursor-pointer`}
              >
                <i className={`${isDarkMode ? 'ri-sun-line' : 'ri-moon-line'} mr-2`}></i>
                {isDarkMode ? 'Light Mode' : 'Dark Mode'}
              </button>
              <button
                onClick={handleLogout}
                className="block w-full text-left py-2 text-red-600 hover:text-red-700 transition-colors font-medium cursor-pointer"
              >
                <i className="ri-logout-box-line mr-2"></i>
                Logout
              </button>
            </div>
          </motion.div>
        )}
      </nav>

      {/* Main Content */}
      <div className="max-w-[1750px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8 text-center">
          <h1 className={`text-3xl font-bold ${isDarkMode ? 'text-gray-200' : 'text-gray-900'}`}>On Hold Dashboard</h1>
          <p className={`mt-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Overview of clients currently on hold</p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal"></div>
          </div>
        ) : (
          <>
            {/* Client List */}
            {clients.length > 0 && (
              <div className="mb-8">
                {/* Export Button */}
                <div className="mb-4 flex justify-end">
                  <button
                    onClick={handleExportToExcel}
                    className={`${isDarkMode ? 'bg-green-700 hover:bg-green-800' : 'bg-green-600 hover:bg-green-700'} text-white px-5 py-2 rounded-lg font-medium transition-colors whitespace-nowrap cursor-pointer flex items-center gap-2 text-sm`}
                  >
                    <i className="ri-file-excel-2-line text-base"></i>
                    Export to Excel
                  </button>
                </div>

                <div className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-xl shadow-sm border overflow-hidden`}>
                  <div className="overflow-x-auto">
                    {/* Desktop Table */}
                    <table className="w-full hidden md:table" style={{ fontSize: '9.375px' }}>
                      <thead className={`${isDarkMode ? 'bg-gray-900 border-gray-700' : 'bg-gray-50 border-gray-200'} border-b`}>
                        <tr>
                          <th className={`px-2 py-2 text-center font-semibold ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`} style={{ fontSize: '9.375px', width: '10%' }}>Client Name</th>
                          <th className={`px-2 py-2 text-center font-semibold ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`} style={{ fontSize: '9.375px', width: '6%' }}>State</th>
                          <th className={`px-2 py-2 text-center font-semibold ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`} style={{ fontSize: '9.375px', width: '7.5%' }}>Purchase Price</th>
                          <th className={`px-2 py-2 text-center font-semibold ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`} style={{ fontSize: '9.375px', width: '7.5%' }}>Loan Amount</th>
                          <th className={`px-2 py-2 text-center font-semibold ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`} style={{ fontSize: '9.375px', width: '8%' }}>Loan Type</th>
                          <th className={`px-2 py-2 text-center font-semibold ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`} style={{ fontSize: '9.375px', width: '7%' }}>Lender</th>
                          <th className={`px-2 py-2 text-center font-semibold ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`} style={{ fontSize: '9.375px', width: '7%' }}>Broker</th>
                          <th className={`px-2 py-2 text-center font-semibold ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`} style={{ fontSize: '9.375px', width: '9%' }}>Follow-Up Date</th>
                          <th className={`px-2 py-2 text-center font-semibold ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`} style={{ fontSize: '9.375px', width: '10%' }}>Client State</th>
                          <th className={`px-2 py-2 text-center font-semibold ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`} style={{ fontSize: '9.375px', width: '20%' }}>Notes</th>
                          <th className={`px-2 py-2 text-center font-semibold ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`} style={{ fontSize: '9.375px', width: '8%' }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody className={`${isDarkMode ? 'divide-gray-700' : 'divide-gray-200'} divide-y`}>
                        {clients.map((client, index) => (
                          <tr key={client.id} className={`transition-colors ${
                            isDarkMode 
                              ? index % 2 === 0 ? 'bg-gray-800 hover:bg-gray-700' : 'bg-gray-900 hover:bg-gray-700'
                              : index % 2 === 0 ? 'bg-white hover:bg-gray-50' : 'bg-gray-50 hover:bg-gray-100'
                          }`}>
                            <td className="px-2 py-2 text-center">
                              <button
                                onClick={() => handleClientNameClick(client)}
                                className="font-medium text-teal hover:text-teal/80 underline cursor-pointer"
                                style={{ fontSize: '9.375px' }}
                              >
                                {client.name}
                              </button>
                            </td>
                            <td className="px-2 py-2 text-center">
                              <span className={`${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`} style={{ fontSize: '9.375px' }}>{client.state || '-'}</span>
                            </td>
                            <td className="px-2 py-2 text-center">
                              <span className={`font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-900'}`} style={{ fontSize: '9.375px' }}>
                                {formatCurrency(parseFloat(client.purchase_price) || 0)}
                              </span>
                            </td>
                            <td className="px-2 py-2 text-center">
                              <span className={`font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-900'}`} style={{ fontSize: '9.375px' }}>
                                {formatCurrency(parseFloat(client.loan_amount) || 0)}
                              </span>
                            </td>
                            <td className="px-2 py-2 text-center">
                              <span className={`${isDarkMode ? 'text-gray-300' : 'text-gray-500'}`} style={{ fontSize: '9.375px' }}>
                                {client.loan_type || 'N/A'}
                              </span>
                            </td>
                            <td className="px-2 py-2 text-center">
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                client.lender_name === 'NS' 
                                  ? 'bg-blue-100 text-blue-800' 
                                  : 'bg-yellow-100 text-yellow-800'
                              }`} style={{ fontSize: '9.375px' }}>
                                {client.lender_name || '-'}
                              </span>
                            </td>
                            <td className="px-2 py-2 text-center">
                              <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full font-medium ${
                                client.broker === 'NS' 
                                  ? 'bg-black text-[#54a7dc]' 
                                  : 'bg-[#f3e8c5] text-[#1c272b]'
                              }`} style={{ fontSize: '9.375px' }}>
                                {client.broker}
                              </span>
                            </td>
                            <td className="px-2 py-2 text-center">
                              {editingId === client.id ? (
                                <input
                                  type="date"
                                  value={client.follow_up_date || ''}
                                  onChange={(e) => handleChange(client.id, 'follow_up_date', e.target.value)}
                                  className={`w-full px-2 py-1 border ${isDarkMode ? 'border-gray-600 bg-[#394150] text-[#e6e7eb]' : 'border-gray-300'} rounded-md focus:outline-none focus:ring-2 focus:ring-teal text-center`}
                                  style={{ fontSize: '9.375px' }}
                                />
                              ) : (
                                <span className={`${isDarkMode ? 'text-gray-300' : 'text-gray-900'}`} style={{ fontSize: '9.375px' }}>
                                  {client.follow_up_date ? formatDate(client.follow_up_date) : '-'}
                                </span>
                              )}
                            </td>
                            <td className="px-2 py-2 text-center">
                              {editingId === client.id ? (
                                <select
                                  value={client.client_state}
                                  onChange={(e) => handleChange(client.id, 'client_state', e.target.value)}
                                  className={`px-1.5 py-0.5 border ${isDarkMode ? 'border-gray-600 bg-[#394150] text-[#e6e7eb]' : 'border-gray-300'} rounded-md focus:outline-none focus:ring-2 focus:ring-teal cursor-pointer text-center`}
                                  style={{ fontSize: '9.375px' }}
                                >
                                  {clientStateOptions.map((option) => (
                                    <option key={option} value={option}>{option}</option>
                                  ))}
                                </select>
                              ) : (
                                <span className={`${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`} style={{ fontSize: '9.375px' }}>
                                  {client.client_state}
                                </span>
                              )}
                            </td>
                            <td className="px-2 py-2 text-center">
                              {editingId === client.id ? (
                                <textarea
                                  value={client.notes || ''}
                                  onChange={(e) => handleChange(client.id, 'notes', e.target.value)}
                                  className={`w-full px-2 py-1 border ${isDarkMode ? 'border-gray-600 bg-[#394150] text-[#e6e7eb]' : 'border-gray-300'} rounded-md focus:outline-none focus:ring-2 focus:ring-teal resize-none`}
                                  style={{ fontSize: '9.375px' }}
                                  rows={2}
                                />
                              ) : (
                                <span className={`${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`} style={{ fontSize: '9.375px' }}>
                                  {client.notes || '-'}
                                </span>
                              )}
                            </td>
                            <td className="px-2 py-2">
                              <div className="flex items-center justify-center gap-1">
                                {editingId === client.id ? (
                                  <>
                                    <button
                                      onClick={() => handleSave(client.id)}
                                      className="w-5 h-5 flex items-center justify-center bg-green-100 text-green-600 rounded-lg hover:bg-green-200 transition-colors cursor-pointer"
                                      title="Save"
                                    >
                                      <i className="ri-check-line text-xs"></i>
                                    </button>
                                    <button
                                      onClick={() => setEditingId(null)}
                                      className="w-5 h-5 flex items-center justify-center bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 transition-colors cursor-pointer"
                                      title="Cancel"
                                    >
                                      <i className="ri-close-line text-xs"></i>
                                    </button>
                                  </>
                                ) : (
                                  <button
                                    onClick={() => handleEdit(client.id)}
                                    className="w-5 h-5 flex items-center justify-center bg-blue-100 text-blue-600 rounded-lg hover:bg-blue-200 transition-colors cursor-pointer"
                                    title="Edit"
                                  >
                                    <i className="ri-edit-line text-xs"></i>
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>

                    {/* Mobile View */}
                    <div className="md:hidden">
                      {clients.map((client, index) => (
                        <div
                          key={client.id}
                          className={`p-4 border-b ${
                            isDarkMode
                              ? index % 2 === 0 ? 'bg-gray-800 border-gray-700' : 'bg-gray-900 border-gray-700'
                              : index % 2 === 0 ? 'bg-white border-gray-200' : 'bg-gray-50 border-gray-200'
                          }`}
                        >
                          <div className="flex items-center justify-between mb-2 gap-2">
                            <span className={`font-semibold ${isDarkMode ? 'text-gray-200' : 'text-gray-900'} text-sm truncate flex-1`}>
                              {client.name}
                            </span>
                            <span className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} flex-shrink-0`}>
                              {client.state || '-'}
                            </span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                              Follow-Up: {client.follow_up_date ? formatDate(client.follow_up_date) : '-'}
                            </span>
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full font-medium text-xs ${
                              client.broker === 'NS' 
                                ? 'bg-black text-[#54a7dc]' 
                                : 'bg-[#f3e8c5] text-[#1c272b]'
                            }`}>
                              {client.broker}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Stats - Moved below table */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-xl shadow-sm border p-6`}>
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-yellow-100 rounded-lg">
                    <i className="ri-pause-circle-line text-2xl text-yellow-600"></i>
                  </div>
                  <div>
                    <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'} font-medium`}>Total On Hold Clients</p>
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Clients:</span>
                    <span className={`text-2xl font-bold ${isDarkMode ? 'text-gray-200' : 'text-gray-900'}`}>{totalOnHold}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Purchase Price Total:</span>
                    <span className="text-xl font-bold text-yellow-600">{formatCurrency(totalPurchasePrice)}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Loan Amount Total:</span>
                    <span className="text-xl font-bold text-yellow-600">{formatCurrency(totalLoanAmount)}</span>
                  </div>
                </div>
              </div>
              
              <div className="bg-black rounded-xl shadow-sm border border-gray-800 p-6">
                <div className="flex items-center gap-4 mb-4">
                  <div>
                    <p className="text-lg font-semibold" style={{ color: '#54a7dc' }}>NS (Next Step Lending)</p>
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-400">Clients:</span>
                    <span className="text-2xl font-bold text-white">{nsClients.length}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-400">Purchase Price Total:</span>
                    <span className="text-xl font-bold" style={{ color: '#54a7dc' }}>{formatCurrency(nsPurchasePrice)}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-400">Loan Amount Total:</span>
                    <span className="text-xl font-bold" style={{ color: '#54a7dc' }}>{formatCurrency(nsLoanAmount)}</span>
                  </div>
                </div>
              </div>
              
              <div className="rounded-xl shadow-sm border border-gray-200 p-6" style={{ backgroundColor: '#f3e8c5' }}>
                <div className="flex items-center gap-4 mb-4">
                  <div>
                    <p className="text-lg font-semibold" style={{ color: '#1c272b' }}>TM (Trademark Financing)</p>
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm" style={{ color: '#1c272b' }}>Clients:</span>
                    <span className="text-2xl font-bold" style={{ color: '#1c272b' }}>{tmClients.length}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm" style={{ color: '#1c272b' }}>Purchase Price Total:</span>
                    <span className="text-xl font-bold" style={{ color: '#1c272b' }}>{formatCurrency(tmPurchasePrice)}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm" style={{ color: '#1c272b' }}>Loan Amount Total:</span>
                    <span className="text-xl font-bold" style={{ color: '#1c272b' }}>{formatCurrency(tmLoanAmount)}</span>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Client Info Dialog */}
      {showClientInfoDialog && clientInfoDialogClient && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-gray-900">Client Information</h3>
                <button
                  onClick={() => setShowClientInfoDialog(false)}
                  className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
                >
                  <i className="ri-close-line text-2xl"></i>
                </button>
              </div>
            </div>
            <div className="p-6">
              <div className="mb-4">
                <h4 className="text-lg font-semibold text-gray-900 mb-4">{clientInfoDialogClient.name}</h4>
              </div>

              {/* Email Field */}
              <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">Email</label>
                  <div className="flex items-center gap-2">
                    {!editingClientInfoEmail && (
                      <button
                        onClick={() => setEditingClientInfoEmail(true)}
                        className="text-xs text-teal hover:text-teal/80 font-medium cursor-pointer"
                      >
                        <i className="ri-edit-line mr-1"></i>
                        Edit
                      </button>
                    )}
                  </div>
                </div>
                {editingClientInfoEmail ? (
                  <input
                    type="email"
                    value={clientInfoEmail}
                    onChange={(e) => setClientInfoEmail(e.target.value)}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal text-sm"
                  />
                ) : (
                  <p className="text-sm text-gray-900">{clientInfoEmail}</p>
                )}
              </div>

              {/* Phone Field */}
              <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">Phone</label>
                  <div className="flex items-center gap-2">
                    {!editingClientInfoPhone && (
                      <button
                        onClick={() => setEditingClientInfoPhone(true)}
                        className="text-xs text-teal hover:text-teal/80 font-medium cursor-pointer"
                      >
                        <i className="ri-edit-line mr-1"></i>
                        Edit
                      </button>
                    )}
                  </div>
                </div>
                {editingClientInfoPhone ? (
                  <input
                    type="tel"
                    value={clientInfoPhone}
                    onChange={(e) => handleClientInfoPhoneChange(e.target.value)}
                    placeholder="(###)-###-####"
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal text-sm"
                  />
                ) : (
                  <a 
                    href={`tel:${clientInfoPhone.replace(/\D/g, '')}`}
                    className="text-sm text-teal hover:text-teal/80 font-medium cursor-pointer underline"
                  >
                    {clientInfoPhone}
                  </a>
                )}
              </div>

              {/* Action Buttons */}
              {(editingClientInfoEmail || editingClientInfoPhone) && (
                <div className="flex gap-3 mb-6">
                  <button
                    onClick={handleSaveClientInfo}
                    className="flex-1 bg-teal text-white px-6 py-2.5 rounded-lg font-medium hover:bg-teal/90 transition-colors whitespace-nowrap cursor-pointer text-sm"
                  >
                    <i className="ri-check-line mr-2"></i>
                    Save Changes
                  </button>
                  <button
                    onClick={() => {
                      setEditingClientInfoEmail(false);
                      setEditingClientInfoPhone(false);
                      setClientInfoEmail(clientInfoDialogClient.email);
                      setClientInfoPhone(clientInfoDialogClient.phone);
                    }}
                    className="flex-1 bg-gray-200 text-gray-700 px-6 py-2.5 rounded-lg font-medium hover:bg-gray-300 transition-colors whitespace-nowrap cursor-pointer text-sm"
                  >
                    Cancel
                  </button>
                </div>
              )}

              {/* Notes Field */}
              <div className="mb-6">
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">Notes</label>
                </div>
                
                {/* Show old notes from clients.notes field if they exist */}
                {clientInfoDialogClient.notes && clientInfoDialogClient.notes.trim() !== '' && (
                  <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <div className="flex items-start gap-2 mb-2">
                      <i className="ri-information-line text-yellow-600 text-lg mt-0.5"></i>
                      <div className="flex-1">
                        <p className="text-sm font-semibold text-yellow-900 mb-1">Legacy Note Found</p>
                        <p className="text-xs text-yellow-700 mb-2">This note was saved in the old format. You can migrate it to the new notes system below.</p>
                        <div className="bg-white p-3 rounded border border-yellow-300 mb-2">
                          <p className="text-sm text-gray-900 whitespace-pre-wrap">{clientInfoDialogClient.notes}</p>
                        </div>
                        <button
                          onClick={async () => {
                            if (!clientInfoDialogClient.notes) return;
                            
                            const { data: { session } } = await supabase.auth.getSession();
                            if (!session) return;

                            // Add the old note to the new notes system
                            const { data, error } = await supabase
                              .from('client_notes')
                              .insert({
                                client_id: clientInfoDialogClient.id,
                                note: clientInfoDialogClient.notes.trim(),
                                user_id: session.user.id
                              })
                              .select()
                              .single();

                            if (error) {
                              console.error('Error migrating note:', error);
                              alert('Failed to migrate note');
                              return;
                            }

                            if (data) {
                              // Clear the old notes field
                              const { error: updateError } = await supabase
                                .from('clients')
                                .update({ notes: null })
                                .eq('id', clientInfoDialogClient.id);

                              if (updateError) {
                                console.error('Error clearing old note:', updateError);
                              }

                              // Update local state
                              setClientNotes([data, ...clientNotes]);
                              setClientInfoDialogClient({ ...clientInfoDialogClient, notes: '' });
                              setClients(clients.map(c => 
                                c.id === clientInfoDialogClient.id ? { ...c, notes: '' } : c
                              ));
                              
                              alert('Note migrated successfully!');
                            }
                          }}
                          className="bg-yellow-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-yellow-700 transition-colors whitespace-nowrap cursor-pointer text-sm"
                        >
                          <i className="ri-arrow-right-line mr-2"></i>
                          Migrate to New Notes System
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Add New Note */}
                <div className="mb-3">
                  <textarea
                    value={newNoteText}
                    onChange={(e) => setNewNoteText(e.target.value)}
                    placeholder="Add a new note..."
                    rows={3}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal text-sm resize-none"
                  />
                  <button
                    onClick={handleAddNote}
                    disabled={!newNoteText.trim()}
                    className="mt-2 bg-teal text-white px-4 py-2 rounded-lg font-medium hover:bg-teal/90 transition-colors whitespace-nowrap cursor-pointer text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <i className="ri-add-line mr-2"></i>
                    Add Note
                  </button>
                </div>

                {/* Notes List */}
                <div className="space-y-3 max-h-80 overflow-y-auto">
                  {clientNotes.length > 0 ? (
                    clientNotes.map((note) => (
                      <div
                        key={note.id}
                        className="border border-gray-200 rounded-lg p-3 bg-gray-50"
                      >
                        {editingNoteId === note.id ? (
                          <div>
                            <textarea
                              value={editingNoteText}
                              onChange={(e) => setEditingNoteText(e.target.value)}
                              rows={3}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal text-sm resize-none mb-2"
                            />
                            <div className="flex gap-2">
                              <button
                                onClick={() => handleSaveNote(note.id)}
                                className="bg-green-600 text-white px-3 py-1.5 rounded-lg font-medium hover:bg-green-700 transition-colors whitespace-nowrap cursor-pointer text-xs"
                              >
                                <i className="ri-check-line mr-1"></i>
                                Save
                              </button>
                              <button
                                onClick={handleCancelEditNote}
                                className="bg-gray-200 text-gray-700 px-3 py-1.5 rounded-lg font-medium hover:bg-gray-300 transition-colors whitespace-nowrap cursor-pointer text-xs"
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div>
                            <p className="text-sm text-gray-900 whitespace-pre-wrap mb-2">
                              {note.note}
                            </p>
                            <div className="flex items-center justify-between">
                              <div className="text-xs text-gray-500">
                                <div>Created: {formatDateTime(note.created_at)}</div>
                                {note.updated_at && note.updated_at !== note.created_at && (
                                  <div>Updated: {formatDateTime(note.updated_at)}</div>
                                )}
                              </div>
                              <div className="flex gap-2">
                                <button
                                  onClick={() => handleEditNote(note)}
                                  className="text-blue-600 hover:text-blue-700 transition-colors cursor-pointer text-xs"
                                >
                                  <i className="ri-edit-line mr-1"></i>
                                  Edit
                                </button>
                                <button
                                  onClick={() => handleDeleteNote(note.id)}
                                  className="text-red-600 hover:text-red-700 transition-colors cursor-pointer text-xs"
                                >
                                  <i className="ri-delete-bin-line mr-1"></i>
                                  Delete
                                </button>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-4 bg-gray-50 rounded-lg">
                      <p className="text-sm text-gray-500">No notes added yet</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Sent Templates Section */}
              <div className="pt-6 border-t border-gray-200">
                <h5 className="text-sm font-semibold text-gray-900 mb-3">Sent Templates History</h5>
                {sentTemplates.length > 0 ? (
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {sentTemplates.map((template) => (
                      <div
                        key={template.id}
                        className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                      >
                        <div className="flex items-center gap-3 flex-1">
                          <div className={`w-8 h-8 flex items-center justify-center rounded-lg ${
                            template.template_type === 'email' 
                              ? 'bg-teal/10 text-teal' 
                              : 'bg-purple-100 text-purple-600'
                          }`}>
                            <i className={`${
                              template.template_type === 'email' 
                                ? 'ri-mail-line' 
                                : 'ri-message-3-line'
                            } text-sm`}></i>
                          </div>
                          <div className="flex-1">
                            <p className="text-sm font-medium text-gray-900">{template.template_name}</p>
                            <p className="text-xs text-gray-500">
                              {formatDateTime(template.sent_at)}
                            </p>
                          </div>
                          <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                            template.template_type === 'email'
                              ? 'bg-teal/10 text-teal'
                              : 'bg-purple-100 text-purple-600'
                          }`}>
                            {template.template_type === 'email' ? 'Email' : 'Text'}
                          </span>
                        </div>
                        <button
                          onClick={() => handleDeleteSentTemplate(template.id)}
                          className="ml-3 w-7 h-7 flex items-center justify-center bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition-colors cursor-pointer"
                          title="Delete"
                        >
                          <i className="ri-delete-bin-line text-xs"></i>
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-6 bg-gray-50 rounded-lg">
                    <i className="ri-inbox-line text-3xl text-gray-300 mb-2"></i>
                    <p className="text-sm text-gray-500">No templates sent yet</p>
                  </div>
                )}
              </div>

              {/* Discovery Calls Section */}
              <div className="pt-6 border-t border-gray-200">
                <h5 className="text-sm font-semibold text-gray-900 mb-3">Discovery Calls</h5>
                {clientDiscoveryCalls.length > 0 ? (
                  <div className="space-y-3 max-h-60 overflow-y-auto">
                    {clientDiscoveryCalls.map((call) => (
                      <div
                        key={call.id}
                        className="border border-gray-200 rounded-lg p-3 bg-gray-50"
                      >
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs font-semibold text-gray-900">
                            Call Date: {formatDate(call.call_date)}
                          </span>
                          <span className="text-xs text-gray-500">
                            Logged: {formatDateTime(call.created_at)}
                          </span>
                        </div>
                        <div className="grid grid-cols-2 gap-2 text-xs">
                          <div>
                            <span className="font-medium text-gray-700">Housing:</span>
                            <span className="text-gray-600 ml-1">{call.current_housing || '-'}</span>
                          </div>
                          <div>
                            <span className="font-medium text-gray-700">Timeline:</span>
                            <span className="text-gray-600 ml-1">{call.purchase_timeline || '-'}</span>
                          </div>
                          <div>
                            <span className="font-medium text-gray-700">Pre-approval:</span>
                            <span className="text-gray-600 ml-1">{call.preapproval_status || '-'}</span>
                          </div>
                          <div>
                            <span className="font-medium text-gray-700">Budget:</span>
                            <span className="text-gray-600 ml-1">{call.budget_down_payment || '-'}</span>
                          </div>
                          <div>
                            <span className="font-medium text-gray-700">Credit:</span>
                            <span className="text-gray-600 ml-1">{call.credit_score_range || '-'}</span>
                          </div>
                          <div>
                            <span className="font-medium text-gray-700">Employment:</span>
                            <span className="text-gray-600 ml-1">{call.employment_status || '-'}</span>
                          </div>
                        </div>
                        {call.additional_notes && (
                          <div className="mt-2 pt-2 border-t border-gray-200">
                            <span className="text-xs font-medium text-gray-700">Notes:</span>
                            <p className="text-xs text-gray-600 mt-1 whitespace-pre-wrap">{call.additional_notes}</p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-6 bg-gray-50 rounded-lg">
                    <i className="ri-phone-line text-3xl text-gray-300 mb-2"></i>
                    <p className="text-sm text-gray-500">No discovery calls logged yet</p>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default OnHoldPage;
